/**
 * 将既有模块挂到 ipeUserLocalState（副作用注册）。
 * 由 IpeAuthContext 在启动时 import，保证 logout 前 cleanup 已注册。
 */
import {
  onIpeUserLocalOwnerChange,
  registerLegacyUserLocalKeys,
  registerUserLocalCleanup,
} from '@/utils/ipeUserLocalState';
import {
  bindDownloadCenterOwner,
  clearDownloadCenterForLogout,
  resetDownloadCenterBlobStoreForOwnerChange,
} from '@/utils/downloadCenter';
import { clearImageSpaceCacheForLogout } from '@/pages/ipe/workbench/panels/imageSpaceUtils';
import { clearImageSpaceFoldersForLogout } from '@/pages/ipe/workbench/utils/imageSpaceMeta';
import { clearMaterialHubFavoritesForLogout } from '@/pages/ipe/workbench/utils/materialHubFavorites';
import { clearSourcingWorkflowRunHistoryForLogout } from '@/pages/ipe/workbench/utils/sourcingWorkflowRunHistory';
import { clearPlatformListingProductMetaForLogout } from '@/pages/ipe/workbench/utils/platformListingProductMeta';
import { clearReceiveItemMetaForLogout } from '@/pages/ipe/workbench/utils/receiveItemProductMeta';
import { clearWorkflowTasksForLogout } from '@/pages/ipe/workbench/constants/workflowTaskQueue';
import { clearWorkflowTemplatesForLogout } from '@/pages/ipe/workbench/constants/ipeWorkflowStorage';
import { clearTemplateEditorLocalForLogout } from '@/pages/ipe/workbench/utils/templateEditorLocalStorage';
import { clearManagedViewImageLoadCache } from '@/utils/managedViewImageLoadCache';
import { clearExportExternalUrlCache } from '@/utils/exportPublicImageUrl';
import { clearManagedImageDisplayCachesForLogout } from '@/utils/imageDisplayAccess';
import { clearAgentPendingChatForLogout } from '@/config/agentModes';
import { dropLegacyUnscopedIndexedDatabases } from '@/utils/ipeUserIndexedDb';
import { resetImageStorageForOwnerChange } from '@/utils/imageStorage';
import { resetFileStorageForOwnerChange } from '@/utils/fileStorage';
import { resetTextStorageForOwnerChange } from '@/utils/textStorage';

/**
 * 旧全局 key 清单：登录 bind 时统一丢弃，绝不迁入当前用户命名空间。
 * 下载中心旧自管 key `funding-download-center-v1:u:{id}` 由 bindDownloadCenterOwner 迁入 userLocal 后删除，
 * 此处不按前缀批量删，以免未登录用户的数据在迁入前被清掉。
 */
registerLegacyUserLocalKeys({
  keys: [
    'funding-download-center-v1',
    'funding-download-center-v1:anon',
    'ipe-image-space-cache-v2-personal',
    'ipe-image-space-cache-v2-team',
    'funding-image-space-cache-v1',
    'ipe-image-space-folders-v1',
    'ipe-material-hub-favorites-v1',
    'ipe-sourcing-workflow-last-run-v1',
    'ipe-platform-pod-product-meta-v1',
    'ipe-product-library-tag-catalog-v1',
    'ipe-workflow-tasks-v2',
    'ipe-workflow-tasks-deleted-ids-v1',
    'ipe-workflow-templates-v1',
    'ipe-workflow-templates-deleted-ids-v1',
    'ipe-workflow-custom-groups-v1',
    'ipe:managed-image-display-cache',
    'ipe:managed-image-display-cache:v1',
    'ipe.agent.pendingChat',
    // pendingListingTemplate 仍是编辑页活跃草稿，不可在每次 bind 时当 legacy 清掉
  ],
  prefixes: [
    'funding_template_editor_',
    'ipe-image-space-folders-v1-team-',
    'ipe.agent.pendingChat.lock.',
  ],
  sessionKeys: [
    'ipe-workflow-tasks-deleted-ids-v1',
    'ipe:managed-image-display-cache',
    'ipe.goodsPrintUrlByImageId',
    'ipe.sourceCutoutTaskId',
  ],
  sessionPrefixes: [
    'ipe-wf-material-queue-v1:',
  ],
});

registerUserLocalCleanup(() => {
  try {
    sessionStorage.removeItem('pendingListingTemplate');
  } catch {
    /* ignore */
  }
});
registerUserLocalCleanup(() => {
  try {
    localStorage.removeItem('pendingListingTemplate');
  } catch {
    /* ignore */
  }
});
registerUserLocalCleanup(clearDownloadCenterForLogout);
registerUserLocalCleanup(resetDownloadCenterBlobStoreForOwnerChange);
registerUserLocalCleanup(clearImageSpaceCacheForLogout);
registerUserLocalCleanup(clearImageSpaceFoldersForLogout);
registerUserLocalCleanup(clearMaterialHubFavoritesForLogout);
registerUserLocalCleanup(clearSourcingWorkflowRunHistoryForLogout);
registerUserLocalCleanup(clearPlatformListingProductMetaForLogout);
registerUserLocalCleanup(clearReceiveItemMetaForLogout);
registerUserLocalCleanup(clearWorkflowTasksForLogout);
registerUserLocalCleanup(clearWorkflowTemplatesForLogout);
registerUserLocalCleanup(clearTemplateEditorLocalForLogout);
registerUserLocalCleanup(clearManagedViewImageLoadCache);
registerUserLocalCleanup(clearExportExternalUrlCache);
registerUserLocalCleanup(clearManagedImageDisplayCachesForLogout);
registerUserLocalCleanup(clearAgentPendingChatForLogout);
registerUserLocalCleanup(resetImageStorageForOwnerChange);
registerUserLocalCleanup(resetFileStorageForOwnerChange);
registerUserLocalCleanup(resetTextStorageForOwnerChange);

onIpeUserLocalOwnerChange((userId) => {
  // 换号时先断开旧连接，避免复用上一账号的 IDB 句柄
  resetImageStorageForOwnerChange();
  resetFileStorageForOwnerChange();
  resetTextStorageForOwnerChange();
  resetDownloadCenterBlobStoreForOwnerChange();
  if (userId) {
    // 旧全局库归属不明：只删不迁，禁止被新账号读到
    dropLegacyUnscopedIndexedDatabases();
    bindDownloadCenterOwner(userId);
  }
});
